/**
 * US-state geometry helpers.
 *
 * Converts the bundled `us-atlas/states-10m` TopoJSON into a GeoJSON
 * FeatureCollection (consumed by Leaflet's <GeoJSON> layer) and a
 * matching list of label anchors (FIPS code, two-letter abbreviation,
 * centroid lat/lng).
 *
 * Resolved once at module load — the data is static.
 */
import { geoCentroid } from "d3-geo";
import { feature } from "topojson-client";
import statesTopology from "us-atlas/states-10m.json";

// us-atlas ships untyped JSON; topojson-client expects the wider Topology shape.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const topo = statesTopology as any;

export const STATE_FEATURES: GeoJSON.FeatureCollection = (() => {
    try {
        return feature(topo, topo.objects.states) as unknown as GeoJSON.FeatureCollection;
    } catch {
        return { type: "FeatureCollection", features: [] };
    }
})();

const STATE_ABBR_BY_FIPS: Record<string, string> = {
    "01": "AL",
    "02": "AK",
    "04": "AZ",
    "05": "AR",
    "06": "CA",
    "08": "CO",
    "09": "CT",
    "10": "DE",
    "11": "DC",
    "12": "FL",
    "13": "GA",
    "15": "HI",
    "16": "ID",
    "17": "IL",
    "18": "IN",
    "19": "IA",
    "20": "KS",
    "21": "KY",
    "22": "LA",
    "23": "ME",
    "24": "MD",
    "25": "MA",
    "26": "MI",
    "27": "MN",
    "28": "MS",
    "29": "MO",
    "30": "MT",
    "31": "NE",
    "32": "NV",
    "33": "NH",
    "34": "NJ",
    "35": "NM",
    "36": "NY",
    "37": "NC",
    "38": "ND",
    "39": "OH",
    "40": "OK",
    "41": "OR",
    "42": "PA",
    "44": "RI",
    "45": "SC",
    "46": "SD",
    "47": "TN",
    "48": "TX",
    "49": "UT",
    "50": "VT",
    "51": "VA",
    "53": "WA",
    "54": "WV",
    "55": "WI",
    "56": "WY",
};

export interface StateLabel {
    fips: string;
    abbr: string;
    lat: number;
    lng: number;
}

export const STATE_LABELS: StateLabel[] = STATE_FEATURES.features
    .map<StateLabel | null>((feat) => {
        const fips = String(feat.id ?? "").padStart(2, "0");
        const abbr = STATE_ABBR_BY_FIPS[fips];
        if (!abbr) return null;
        const [lng, lat] = geoCentroid(feat as never) as [number, number];
        if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
        return { fips, abbr, lat, lng };
    })
    .filter((x): x is StateLabel => x !== null);
